import React from 'react';
import type { UserResponse } from '@troia-v3/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { StatusPill, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

/**
 * Card de usuário (view "Cards"). Padrão-lista: card flat/borda SEM checkbox
 * (F34 — seleção em massa só na tabela) + menu ••• de ações + switch inline de
 * status (Switch shared sm — F47). Avatar sem foto = slate.
 */
export interface UserCardProps {
  user: UserResponse;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onAvatarChange?: (id: string, file: File) => void;
  /** Abre o mini-modal de troca de nível para este usuário */
  onChangeLevel?: (id: string) => void;
  /** Reenvia o convite (e-mail de verificação) para este usuário */
  onResendInvite?: (id: string) => void;
  /** Dispara a redefinição de senha deste usuário */
  onResetPassword?: (id: string) => void;
}

export const UserCard: React.FC<UserCardProps> = ({
  user,
  onEdit,
  onDelete,
  onToggleStatus,
  onAvatarChange,
  onChangeLevel,
  onResendInvite,
  onResetPassword,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Último acesso em tempo relativo (mockup: "há 3 dias", não data absoluta)
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    const diff = Date.now() - new Date(dateString).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const d = Math.floor(h / 24);
    if (d < 30) return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `há ${mo} ${mo === 1 ? 'mês' : 'meses'}`;
    const y = Math.floor(mo / 12);
    return `há ${y} ${y === 1 ? 'ano' : 'anos'}`;
  };

  const getUserInitials = () =>
    `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();

  const handleAvatarClick = () => {
    if (onAvatarChange) fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Arquivo muito grande. Máximo é 5MB.');
      return;
    }
    onAvatarChange?.(user.id, file);
    e.target.value = '';
  };

  const isActive = user.status === 'active';

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(user.id) });
  if (onChangeLevel) actions.push({ label: 'Alterar nível', icon: 'shield', onClick: () => onChangeLevel(user.id) });
  if (onResendInvite) actions.push({ label: 'Reenviar convite', icon: 'mail', onClick: () => onResendInvite(user.id) });
  if (onResetPassword) actions.push({ label: 'Redefinir senha', icon: 'key', onClick: () => onResetPassword(user.id) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(user.id, user.status),
    });
  }
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(user.id), variant: 'danger', dividerBefore: true });

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`user-card-${user.id}`}
    >
      <div className="flex items-start gap-3">
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
        <div
          className="relative group/av flex-shrink-0"
          onClick={handleAvatarClick}
          style={{ cursor: onAvatarChange ? 'pointer' : 'default' }}
          data-testid={`user-card-avatar-${user.id}`}
        >
          <Avatar className="w-11 h-11" isSquircle>
            {user.avatar && <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} isSquircle />}
            <AvatarFallback isSquircle>
              <span className="text-white font-semibold text-sm">{getUserInitials()}</span>
            </AvatarFallback>
          </Avatar>
          {onAvatarChange && (
            <div
              className="absolute inset-0 flex items-center justify-center rounded-[24%] opacity-0 group-hover/av:opacity-100 transition-opacity"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            >
              <Icon name="camera" className="w-4 h-4 text-white" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {user.firstName} {user.lastName}
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {user.email}
          </p>
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1">
            <CardActionsMenu actions={actions} size="sm" data-testid={`user-card-actions-${user.id}`} />
          </div>
        )}
      </div>

      {/* Nível + Status (F47: switch inline quando togglável; F62: StatusPill shared) */}
      <div className="mt-3 flex items-center flex-wrap gap-2">
        {user.levelName && (
          <span
            className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
          >
            <Icon name="shield" className="w-3 h-3" />
            {user.levelName}
          </span>
        )}
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`user-card-toggle-${user.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(user.id, user.status)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativo' : 'Inativo'}
            data-testid={`user-card-status-${user.id}`}
          />
        )}
      </div>

      {/* Equipes — cap de 2 chips + "+N" (mockup; sem cap uma linha vira a tela inteira) */}
      {user.teams && user.teams.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {user.teams.slice(0, 2).map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center h-[22px] px-2 rounded-md text-xs font-medium max-w-[140px]"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              <span className="truncate">{t.name}</span>
            </span>
          ))}
          {user.teams.length > 2 && (
            <span
              className="inline-flex items-center h-[22px] px-2 rounded-md text-xs font-semibold"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
              title={user.teams.slice(2).map((t) => t.name).join(', ')}
            >
              +{user.teams.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Footer: último acesso */}
      <div className="mt-3 pt-3 text-xs" style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}>
        Último acesso: {formatDate(user.lastLoginAt)}
      </div>
    </div>
  );
};

UserCard.displayName = 'UserCard';
